import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  BookOpen, Clock, ChevronRight, ChevronDown, Lock, 
  CheckCircle, Play, Award, Users, Loader2, ArrowLeft,
  GraduationCap, Target, FileText, Headphones, Star, Send, Mail, X
} from 'lucide-react';
import ShareButtons from '../components/ShareButtons';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AcademyCourseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: '', email: '' });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [pendingLessonId, setPendingLessonId] = useState(null);

  useEffect(() => {
    fetchCourse();
  }, [slug, user]);

  const fetchCourse = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/academy/course/${slug}`, { headers });
      setCourse(response.data);
      
      // Expand first module by default
      if (response.data.modules?.length > 0) {
        setExpandedModules({ 0: true });
      }
      
      // Fetch reviews
      if (response.data.id) {
        fetchReviews(response.data.id);
      }
    } catch (error) {
      console.error('Failed to fetch course:', error);
      toast.error('Course not found');
      navigate('/academy/courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async (courseId) => {
    try {
      const res = await axios.get(`${API}/academy/reviews/${courseId}`);
      setReviews(res.data.reviews || []);
      setReviewStats({ average_rating: res.data.average_rating, total_reviews: res.data.total_reviews });
    } catch {}
  };

  const submitReview = async () => {
    if (!newReview.comment.trim()) { toast.error('Please write a comment'); return; }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/academy/reviews`, {
        course_id: course.id,
        rating: newReview.rating,
        comment: newReview.comment
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success('Review submitted!');
      setNewReview({ rating: 5, comment: '' });
      fetchReviews(course.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const toggleModule = (index) => {
    setExpandedModules(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const startLesson = (lessonId, isPreview) => {
    if (!user && isPreview) {
      // Not logged in — show lead capture modal
      setPendingLessonId(lessonId);
      setShowLeadModal(true);
      return;
    }
    navigate(`/academy/lesson/${lessonId}`);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    try {
      await axios.post(`${API}/leads/capture`, {
        email: leadForm.email,
        name: leadForm.name,
        course_slug: slug,
        course_title: course?.title || ''
      });
      toast.success('Check your email for an exclusive 50% off discount!');
      setShowLeadModal(false);
      // Redirect to register page with email pre-filled
      navigate(`/register?email=${encodeURIComponent(leadForm.email)}&name=${encodeURIComponent(leadForm.name)}&redirect=/academy/lesson/${pendingLessonId}`);
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!course) return null;

  const totalLessons = course.modules?.reduce((acc, m) => acc + m.lessons.length, 0) || 0;
  const completedLessons = course.user_progress?.completed_lessons?.length || 0;
  const progressPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
  const hasAccess = course.has_access;
  const totalHours = Math.round(course.total_duration / 60);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="academy-course-detail">
      <Navbar />

      {/* Hero */}
      <div className="pt-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button 
            variant="ghost" 
            className="text-slate-400 hover:text-white mb-6 -ml-4"
            onClick={() => navigate('/academy/courses')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to courses
          </Button>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <Badge className="bg-primary/20 text-primary-foreground border-0">
                {course.level}
              </Badge>

              <h1 className="font-heading text-3xl md:text-4xl font-bold">
                {course.title}
              </h1>

              <div className="flex items-center gap-3">
                <ShareButtons title={course.title} description={course.description} />
              </div>

              <p className="text-lg text-slate-300">
                {course.description}
              </p>

              <div className="flex flex-wrap items-center gap-6 text-slate-400">
                <span className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  {course.modules?.length || 0} modules
                </span>
                <span className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {totalLessons} lessons
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {totalHours}h total
                </span>
                <span className="flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  Certificate
                </span>
              </div>

              {/* Progress bar for enrolled users */}
              {hasAccess && progressPercent > 0 && (
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Your Progress</span>
                    <span className="text-sm">{progressPercent}% complete</span>
                  </div>
                  <Progress value={progressPercent} className="h-2" />
                  <p className="text-xs text-slate-400 mt-2">
                    {completedLessons} of {totalLessons} lessons completed
                  </p>
                </div>
              )}
            </div>

            {/* Sidebar Card */}
            <div className="lg:col-span-1">
              <Card className="overflow-hidden sticky top-24 border-0 shadow-2xl">
                <div className="aspect-video relative">
                  <img 
                    src={course.thumbnail_url} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                  {hasAccess && (
                    <div className="absolute inset-0 bg-green-600/90 flex items-center justify-center">
                      <div className="text-center text-white">
                        <CheckCircle className="w-12 h-12 mx-auto mb-2" />
                        <span className="font-semibold">Full Access</span>
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className="p-6 space-y-4">
                  {hasAccess ? (
                    <>
                      <p className="text-center text-green-600 font-medium">
                        You have full access to this course
                      </p>
                      <Button 
                        className="w-full h-12"
                        onClick={() => {
                          const firstLesson = course.modules?.[0]?.lessons?.[0];
                          if (firstLesson) startLesson(firstLesson.id, firstLesson.is_preview);
                        }}
                        data-testid="continue-learning-btn"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        {progressPercent > 0 ? 'Continue Learning' : 'Start Course'}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="text-center">
                        <p className="text-slate-600 mb-2">Unlock this course with</p>
                        <p className="text-3xl font-bold text-slate-900">Membership</p>
                        <p className="text-slate-500">$17/month or $170/year</p>
                      </div>
                      <Button 
                        className="w-full h-12"
                        onClick={() => navigate('/academy/membership')}
                        data-testid="get-membership-btn"
                      >
                        <Lock className="w-5 h-5 mr-2" />
                        Get Membership
                      </Button>
                      <p className="text-xs text-center text-slate-500">
                        First 2 lessons are free to preview
                      </p>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
              Course Curriculum
            </h2>

            <div className="space-y-4">
              {course.modules?.map((module, modIdx) => {
                const isExpanded = expandedModules[modIdx];
                const moduleCompleted = module.lessons.every(l => 
                  course.user_progress?.completed_lessons?.includes(l.id)
                );

                return (
                  <Card key={module.id} className="overflow-hidden">
                    <button
                      onClick={() => toggleModule(modIdx)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          moduleCompleted ? 'bg-green-100' : 'bg-primary/10'
                        }`}>
                          {moduleCompleted ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <span className="font-semibold text-primary">{modIdx + 1}</span>
                          )}
                        </div>
                        <div className="text-left">
                          <h3 className="font-semibold text-slate-900">
                            {module.title}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {module.lessons.length} lessons
                          </p>
                        </div>
                      </div>
                      <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${
                        isExpanded ? 'rotate-180' : ''
                      }`} />
                    </button>

                    {isExpanded && (
                      <div className="border-t border-slate-100">
                        {module.lessons.map((lesson, lesIdx) => {
                          const isCompleted = course.user_progress?.completed_lessons?.includes(lesson.id);
                          const isPreview = lesson.is_preview;
                          const canAccess = hasAccess || isPreview;

                          return (
                            <div
                              key={lesson.id}
                              className={`flex items-center gap-4 p-4 border-b border-slate-100 last:border-0 ${
                                canAccess ? 'hover:bg-slate-50 cursor-pointer' : 'opacity-60'
                              }`}
                              onClick={() => canAccess && startLesson(lesson.id, isPreview)}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isCompleted ? 'bg-green-100' : 'bg-slate-100'
                              }`}>
                                {isCompleted ? (
                                  <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : canAccess ? (
                                  <Play className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <Lock className="w-4 h-4 text-slate-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${isCompleted ? 'text-green-700' : 'text-slate-700'}`}>
                                    {lesson.title}
                                  </span>
                                  {isPreview && !hasAccess && (
                                    <Badge variant="outline" className="text-xs">
                                      Free Preview
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {lesson.duration_minutes}m
                                </span>
                                {canAccess && (
                                  <ChevronRight className="w-4 h-4" />
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>

            {/* Final Quiz Section */}
            {hasAccess && (
              <Card className="mt-8 border-primary/20 bg-primary/5">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Award className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">Final Assessment</h3>
                      <p className="text-sm text-slate-600">
                        Complete all lessons and pass the final quiz to earn your certificate
                      </p>
                    </div>
                    <Button
                      variant={progressPercent === 100 ? "default" : "outline"}
                      disabled={progressPercent < 100}
                      onClick={() => navigate(`/academy/course/${slug}/final-quiz`)}
                    >
                      {progressPercent === 100 ? 'Take Final Quiz' : 'Complete Lessons First'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6">
              <h3 className="font-heading font-semibold text-lg mb-4">This course includes</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {totalLessons} comprehensive lessons
                </li>
                <li className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  {totalHours}+ hours of content
                </li>
                <li className="flex items-center gap-3">
                  <Headphones className="w-5 h-5 text-primary" />
                  Audio versions of lessons
                </li>
                <li className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  Downloadable resources
                </li>
                <li className="flex items-center gap-3">
                  <Target className="w-5 h-5 text-primary" />
                  Quizzes after each lesson
                </li>
                <li className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-primary" />
                  Certificate of completion
                </li>
                <li className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Lifetime access
                </li>
              </ul>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-primary/5 to-purple-50">
              <h3 className="font-heading font-semibold text-lg mb-3">Perfect for</h3>
              <ul className="space-y-2 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Small business owners
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Entrepreneurs and solopreneurs
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Marketing professionals
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Content creators
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  Anyone wanting to learn AI
                </li>
              </ul>
            </Card>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-12" data-testid="reviews-section">
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Student Reviews
            {reviewStats.total_reviews > 0 && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                {reviewStats.average_rating}/5 ({reviewStats.total_reviews} {reviewStats.total_reviews === 1 ? 'review' : 'reviews'})
              </span>
            )}
          </h2>

          {/* Write Review */}
          {user && course?.is_enrolled && (
            <Card className="mb-6 border-primary/20" data-testid="write-review-card">
              <CardContent className="p-5">
                <p className="text-sm font-medium text-slate-700 mb-3">Write a Review</p>
                <div className="flex gap-1 mb-3">
                  {[1,2,3,4,5].map(s => (
                    <button key={s} onClick={() => setNewReview(p => ({...p, rating: s}))} data-testid={`star-${s}`}>
                      <Star className={`w-5 h-5 transition-colors ${s <= newReview.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-300'}`} />
                    </button>
                  ))}
                </div>
                <textarea
                  value={newReview.comment}
                  onChange={e => setNewReview(p => ({...p, comment: e.target.value}))}
                  placeholder="Share your experience with this course..."
                  className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="review-comment-input"
                />
                <Button size="sm" className="mt-2" onClick={submitReview} disabled={submittingReview} data-testid="submit-review-btn">
                  {submittingReview ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Submit Review
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Review List */}
          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map(r => (
                <Card key={r.id} className="border-slate-100" data-testid="review-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary">
                          {(r.user_name || 'S')[0].toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-slate-800">{r.user_name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-3.5 h-3.5 ${s <= r.rating ? 'text-amber-500 fill-amber-500' : 'text-slate-200'}`} />
                          ))}
                        </div>
                      </div>
                      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed">{r.comment}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-8">No reviews yet. Be the first to review this course!</p>
          )}
        </div>
      </div>

      {/* Lead Capture Modal */}
      {showLeadModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="lead-capture-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative">
              <button 
                onClick={() => setShowLeadModal(false)}
                className="absolute top-4 right-4 text-white/70 hover:text-white"
                data-testid="lead-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
              <h3 className="font-heading text-xl font-bold">Start Your Free Preview</h3>
              <p className="text-indigo-200 text-sm mt-1">Register to access free lessons + get 50% off your first month</p>
            </div>
            <form onSubmit={handleLeadSubmit} className="p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <p className="text-amber-800 font-semibold text-sm">50% OFF your first month — just for signing up!</p>
                <p className="text-amber-600 text-xs mt-0.5">Discount code sent to your email instantly</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-name">Your name</Label>
                <Input
                  id="lead-name"
                  placeholder="Enter your name"
                  value={leadForm.name}
                  onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                  required
                  data-testid="lead-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lead-email">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="lead-email"
                    type="email"
                    placeholder="you@example.com"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    required
                    className="pl-10"
                    data-testid="lead-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-indigo-600 hover:bg-indigo-700"
                disabled={leadSubmitting}
                data-testid="lead-submit-btn"
              >
                {leadSubmitting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {leadSubmitting ? 'Processing...' : 'Start Free Preview'}
              </Button>
              <p className="text-xs text-slate-400 text-center">
                Already have an account? <a href="/login" className="text-indigo-600 hover:underline">Sign in</a>
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademyCourseDetail;
